import * as React from 'react';
import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import { AuthState } from '../auth/auth.types';
import { DashboardState, CalendarEvent } from './dashboard.types';
import TextField from 'material-ui/TextField';
import { isAlphanumeric, isValidCalendarEventNote } from '../util/Validation';
import TimePicker from 'material-ui/TimePicker';
import { Event, Color } from '../calendar/calendar.event.types';
import DatePicker from 'material-ui/DatePicker';
import { AdminState } from '../admin/admin.types';
import { cannotDelete, isMemberDisabled } from '../util/permission.util';
import { CALENDAR, STAFF } from '../account/Constants';
import { IdleState } from '../idle/Types';
import { GithubPicker } from 'react-color';
import { Member, MembersState } from '../members/members.types';
import AutoComplete from 'material-ui/AutoComplete';
import Chip from 'material-ui/Chip';
import Avatar from 'material-ui/Avatar';
import RefreshIndicator from 'material-ui/RefreshIndicator';
import { BusinessState } from '../business/business.types';
import { Account } from '../account/Types';

const DISPLAY_TIME_ERROR = 'Pick start & end and check the time range';
const TITLE_NOTE_ERROR = 'Only alpha-numeric characters are allowed';

const styles = {
  swatch: {
    padding: '5px',
    background: '#fff',
    borderRadius: '1px',
    boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
    display: 'inline-block',
    cursor: 'pointer',
  }
};

const DEFAULT_COLOR = '#0b9cd5';

type Props = {
  readonly selectedEvent?: Event
  readonly editMode: boolean
  readonly modalOpen: boolean
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly idleState: IdleState
  readonly membersState: MembersState
  readonly dashboardState: DashboardState
  readonly businessState: BusinessState
  readonly createCalendarEvent: (
    colorValue: string,
    title: string,
    startDate: number,
    endDate: number,
    note: string,
    memberId?: number,
    staffId?: number
  ) => Promise<Response>
  readonly updateCalendarEvent: (
    id: number,
    colorValue: string,
    title: string,
    startDate: number,
    endDate: number,
    note: string,
    memberId?: number,
    staffId?: number
  ) => Promise<Response>
  readonly deleteCalendarEvent: (id: number) => Promise<Response>
  readonly slotInfo?: any
  readonly loadingMembers: boolean
};

type States = {
  readonly open: boolean
  readonly colorValueErrorMessage: string
  readonly title: string
  readonly editedTitle: string
  readonly titleErrorMessage: string
  readonly note: string
  readonly editedNote: string
  readonly noteErrorMessage: string
  readonly eventDate?: Date
  readonly editedEventDate?: Date
  readonly startTime?: Date
  readonly editedStartTime?: Date
  readonly endTime?: Date
  readonly editedEndTime?: Date
  readonly timeErrorMessage: string
  readonly errorExists: boolean
  readonly displayColorPicker: boolean
  readonly colorValue: string
  readonly editedColorValue: string
  readonly selectedMember?: Member
  readonly editedSelectedMember?: Member
  readonly selectedStaff?: Account
  readonly editedSelectedStaff?: Account
  readonly searchText: string
  readonly searchStaffText: string
  readonly dataSource: string[]
  readonly staffDataSource: string[]
};

class CreateEventModalComponent extends React.Component<Props, States> {

  constructor(props: Props) {
    super(props);

    this.state = {
      open: false,
      colorValueErrorMessage: '',
      title: '',
      editedTitle: '',
      titleErrorMessage: '',
      note: '',
      editedNote: '',
      noteErrorMessage: '',
      eventDate: props.slotInfo ? props.slotInfo.start : undefined,
      editedEventDate: props.slotInfo ? props.slotInfo.start : undefined,
      startTime: props.slotInfo ? props.slotInfo.start : undefined,
      editedStartTime: props.slotInfo ? props.slotInfo.start : undefined,
      endTime: props.slotInfo ? props.slotInfo.end : undefined,
      editedEndTime: props.slotInfo ? props.slotInfo.end : undefined,
      timeErrorMessage: '',
      errorExists: false,
      displayColorPicker: false,
      colorValue: DEFAULT_COLOR,
      editedColorValue: DEFAULT_COLOR,
      searchText: '',
      searchStaffText: '',
      dataSource: this.mapMemberToNameAndPhone(props),
      staffDataSource: this.mapStaffToNameAndPhone(props)
    };

    this.handleClose = this.handleClose.bind(this);
    this.handleCreateEvent = this.handleCreateEvent.bind(this);
    this.hadleTitleChange = this.hadleTitleChange.bind(this);
    this.doesErrorExist = this.doesErrorExist.bind(this);
    this.handleStartTimeChange = this.handleStartTimeChange.bind(this);
    this.handleEndTimeChange = this.handleEndTimeChange.bind(this);
    this.handleNoteChange = this.handleNoteChange.bind(this);
    this.createModeButtons = this.createModeButtons.bind(this);
    this.editModeButtons = this.editModeButtons.bind(this);
    this.handleEditEvent = this.handleEditEvent.bind(this);
    this.handleEventDateChange = this.handleEventDateChange.bind(this);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props !== nextProps) {
      const { editMode, modalOpen, selectedEvent } = nextProps;

      if (this.isAnyFieldEdited()) {
        return;
      } else {
        this.setState(
          {
            open: modalOpen,
            title: editMode ? selectedEvent!.title : '',
            editedTitle: editMode ? selectedEvent!.title : '',
            colorValue: editMode ? selectedEvent!.backgroundColor! : DEFAULT_COLOR,
            editedColorValue: editMode ? selectedEvent!.backgroundColor! : DEFAULT_COLOR,
            note: editMode ? selectedEvent!.desc! : '',
            editedNote: editMode ? selectedEvent!.desc! : '',
            eventDate: editMode ? 
              selectedEvent!.start! : 
              nextProps.slotInfo ?
                nextProps.slotInfo.start :
                undefined,
            editedEventDate: editMode ? 
              selectedEvent!.start! : 
              nextProps.slotInfo ?
                nextProps.slotInfo.start :
                undefined,
            startTime: editMode ? 
              selectedEvent!.start! : 
              nextProps.slotInfo ?
                nextProps.slotInfo.start :
                undefined,
            editedStartTime: editMode ? 
              selectedEvent!.start! : 
              nextProps.slotInfo ?
                nextProps.slotInfo.start :
                undefined,
            endTime: editMode ? 
              selectedEvent!.end! : 
              nextProps.slotInfo ?
                nextProps.slotInfo.end :
                undefined,
            editedEndTime: editMode ? 
              selectedEvent!.end! : 
              nextProps.slotInfo ?
                nextProps.slotInfo.end :
                undefined,
            dataSource: this.mapMemberToNameAndPhone(nextProps),
            staffDataSource: this.mapStaffToNameAndPhone(nextProps),
            selectedMember: editMode && 
              selectedEvent && 
              selectedEvent.id !== 0 && 
              nextProps.dashboardState.dashboardCalendarEvents
              .find((c: CalendarEvent) => c.id === selectedEvent.id) ?
                nextProps.membersState.members.find((m: Member) =>
                  m.id === nextProps.dashboardState.dashboardCalendarEvents
                    .find((c: CalendarEvent) => c.id === selectedEvent.id).memberId) :
                undefined,
            editedSelectedMember: editMode && 
              selectedEvent && 
              selectedEvent.id !== 0 &&
              nextProps.dashboardState.dashboardCalendarEvents
              .find((c: CalendarEvent) => c.id === selectedEvent.id) ?
                nextProps.membersState.members.find((m: Member) =>
                  m.id === nextProps.dashboardState.dashboardCalendarEvents
                    .find((c: CalendarEvent) => c.id === selectedEvent.id).memberId) :
                undefined,
            selectedStaff: editMode && 
              selectedEvent && 
              selectedEvent.id !== 0 && 
              nextProps.dashboardState.dashboardCalendarEvents
              .find((c: CalendarEvent) => c.id === selectedEvent.id) ?
                this.getStaffAccounts(nextProps)
                  .find((a: Account) => a.id === nextProps.dashboardState.dashboardCalendarEvents
                    .find((c: CalendarEvent) => c.id === selectedEvent.id).staffId) :
                undefined,
            editedSelectedStaff: editMode && 
              selectedEvent && 
              selectedEvent.id !== 0 && 
              nextProps.dashboardState.dashboardCalendarEvents
              .find((c: CalendarEvent) => c.id === selectedEvent.id) ?
                this.getStaffAccounts(nextProps)
                  .find((a: Account) => a.id === nextProps.dashboardState.dashboardCalendarEvents
                    .find((c: CalendarEvent) => c.id === selectedEvent.id).staffId) :
                undefined
          }
        );
      }
    }
  }

  render() {
    const { editMode } = this.props;
    const { 
      title,
      editedTitle,
      titleErrorMessage,
      open,
      timeErrorMessage,
      noteErrorMessage,
      note,
      editedNote,
      startTime,
      editedStartTime,
      endTime,
      editedEndTime,
      eventDate,
      editedEventDate
    } = this.state;

    return(
      <Dialog
        title={editMode ? 'Edit Event' : 'Create Event'}
        actions={editMode ? 
           this.editModeButtons() :
           this.createModeButtons()
        }
        modal={false}
        open={open}
        onRequestClose={this.handleClose}
        autoDetectWindowHeight={true}
        autoScrollBodyContent={true}
      >
        <div className="row no-gutters align-items-center">
          <div className="col-lg-6">
            <TextField
              hintText="Enter Event Title Here"
              floatingLabelText="Event Title"
              value={editMode ? editedTitle : title}
              errorText={titleErrorMessage}
              onChange={(e: React.FormEvent<{}>, newValue: string) => this.hadleTitleChange(e, newValue, editMode)}
              fullWidth={true}
            />
          </div>
          <div className="col-auto">
            <div style={styles.swatch} onClick={this.handleClick}>
              <div style={this.createColorCss()} />
            </div>
            {
              this.state.displayColorPicker ?
                <div style={{position: 'absolute', zIndex: 2}}>
                  <div
                    style={{
                      position: 'fixed',
                      top: '0px',
                      right: '0px',
                      bottom: '0px',
                      left: '0px',
                    }}
                    onClick={this.handleDisplayColorPickerClose}
                  />
                  <GithubPicker color={this.state.editedColorValue} onChangeComplete={this.handleChange} />
                </div> :
                null
            }
          </div>
        </div>
        <div className="row">
          <div className="col-lg-6">
            <TimePicker
              value={editMode ? 
                editedStartTime : 
                startTime
              }
              hintText="Pick Start Time"
              floatingLabelText="Event Start Time"
              autoOk={true}
              onChange={(e: React.SyntheticEvent<{}>, time: Date) => this.handleStartTimeChange(e, time, editMode)}
              errorText={timeErrorMessage}
            />
          </div>
          <div className="col-lg-6">
            <TimePicker
              value={editMode ? editedEndTime : endTime}
              hintText="Pick End Time"
              floatingLabelText="Event End Time"
              autoOk={true}
              onChange={(e: React.SyntheticEvent<{}>, time: Date) => this.handleEndTimeChange(e, time, editMode)}
              errorText={timeErrorMessage}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-lg-6">
            <DatePicker 
              value={editMode ? editedEventDate : eventDate}
              hintText="Pick Event Date"
              floatingLabelText="Event Date"
              onChange={(e: React.SyntheticEvent<{}>, time: Date) => this.handleEventDateChange(time, editMode)}
              autoOk={true}
            />
          </div>
          <div className="col-lg-6">
            <TextField
              value={editMode ? editedNote : note}
              hintText="Enter Event Note Here"
              floatingLabelText="Event Note"
              multiLine={true}
              rows={2}
              onChange={(e: React.FormEvent<{}>, newValue: string) => this.handleNoteChange(e, newValue, editMode)}
              errorText={noteErrorMessage}
            />
          </div>
        </div>
        <div className="row mt-2">
          <b className="col-auto">
            Member {isMemberDisabled(this.props.authState, this.props.adminState) ? '(Not Enough Permission)' : ''}
          </b>
        </div>
        <div className="row">
          <div className="col-10">
            {
              this.props.loadingMembers ?
                <RefreshIndicator
                  size={40}
                  left={10}
                  top={0}
                  status="loading"
                  style={{
                    display: 'inline-block',
                    position: 'relative',
                  }}
                /> :
                ((this.props.editMode && this.state.editedSelectedMember) || 
                  (!this.props.editMode && this.state.selectedMember)) ?
                  this.renderSelectedMember() :
                  <AutoComplete
                    hintText="Associate Member"
                    floatingLabelText="Associate Member"
                    searchText={this.state.searchText}
                    onUpdateInput={this.handleSearchTextUpdate}
                    dataSource={this.state.dataSource}
                    onNewRequest={this.handleNewRequest}
                    filter={AutoComplete.caseInsensitiveFilter}
                    maxSearchResults={5}
                    fullWidth={true}
                    disabled={isMemberDisabled(this.props.authState, this.props.adminState)}
                  />
            }
          </div>
        </div>
        <div className="row mt-2">
          <b className="col-auto">
            Staff
          </b>
        </div>
        <div className="row">
          <div className="col-10">
            {
              this.props.loadingMembers ?
                <RefreshIndicator
                  size={40}
                  left={10}
                  top={0}
                  status="loading"
                  style={{
                    display: 'inline-block',
                    position: 'relative',
                  }}
                /> :
                ((this.props.editMode && this.state.editedSelectedStaff) || 
                  (!this.props.editMode && this.state.selectedStaff)) ?
                  this.renderSelectedStaff() :
                  <AutoComplete
                    hintText="Associate Staff"
                    floatingLabelText="Associate Staff"
                    searchText={this.state.searchStaffText}
                    onUpdateInput={this.handleSearchStaffTextUpdate}
                    dataSource={this.state.staffDataSource}
                    onNewRequest={this.handleNewStaffRequest}
                    filter={AutoComplete.caseInsensitiveFilter}
                    maxSearchResults={5}
                    fullWidth={true}
                  />
            }
          </div>
        </div>
      </Dialog>
    );
  }

  private getStaffAccounts = (props: Props): Array<Account> => {
    return props.businessState.business ?
      props.businessState.business.accounts
        .filter((a: Account) => a && a.accountType ? a.accountType === STAFF : false) :
      [];
  }

  private handleNewRequest = (chosenRequest: string, index: number): void => {
    this.setState((prevState) =>
      ({
        selectedMember: !this.props.editMode ? this.filterMember(chosenRequest) : undefined,
        editedSelectedMember: this.props.editMode ? this.filterMember(chosenRequest) : prevState.editedSelectedMember
      })
    );
  }

  private handleNewStaffRequest = (chosenRequest: string, index: number): void => {
    this.setState((prevState) =>
      ({
        selectedStaff: !this.props.editMode ? this.filterStaff(chosenRequest) : undefined,
        editedSelectedStaff: this.props.editMode ? this.filterStaff(chosenRequest) : prevState.editedSelectedStaff
      })
    );
  }

  private filterMember = (nameAndPhone: string): Member => {
    return this.props.membersState.members.filter((m: Member) =>
      m.firstName + ' ' + m.lastName + ', ' + m.mobile1 === nameAndPhone
    )[0];
  }

  private filterStaff = (nameAndPhone: string): Account => {
    return this.getStaffAccounts(this.props).filter((a: Account) =>
      a.firstName + ' ' + a.lastName + ', ' + a.phone === nameAndPhone
    )[0];
  }

  private mapMemberToNameAndPhone = (props: Props): string[] => {
    return props.membersState.members.map((m: Member) => m.firstName + ' ' + m.lastName + ', ' + m.mobile1);
  }

  private mapStaffToNameAndPhone = (props: Props): string[] => {
    return this.getStaffAccounts(props).map((a: Account) => a.firstName + ' ' + a.lastName + ', ' + a.phone);
  }

  private handleSearchTextUpdate = (searchText: string): void =>
    this.setState({
      searchText: searchText
    })

  private handleSearchStaffTextUpdate = (searchText: string): void =>
    this.setState({
      searchStaffText: searchText
    })

  private renderSelectedMember = (): JSX.Element => {
    return(
      <Chip
        onRequestDelete={this.handleRequestDelete}
        labelStyle={{height: 50, width: '100%', fontSize: 14}}
        style={{height: 50, width: '100%'}}
      >
        <Avatar
          src={this.state.selectedMember && this.state.selectedMember.picUrl}
          style={{height: 50, width: 50}}
        />
        {
          this.props.editMode ?
            this.state.editedSelectedMember && 
              this.state.editedSelectedMember.firstName + ' ' + this.state.editedSelectedMember.lastName :
            this.state.selectedMember && 
              this.state.selectedMember.firstName + ' ' + this.state.selectedMember.lastName
        } /
        {
          this.props.editMode ?
            this.state.editedSelectedMember && this.state.editedSelectedMember.memberId :
            this.state.selectedMember && this.state.selectedMember.memberId
        }
      </Chip>
    );
  }

  private renderSelectedStaff = (): JSX.Element => {
    return(
      <Chip
        onRequestDelete={this.handleRequestStaffDelete}
        labelStyle={{height: 50, width: '100%', fontSize: 14}}
        style={{height: 50, width: '100%'}}
      >
        {
          this.props.editMode ?
            this.state.editedSelectedStaff && 
              this.state.editedSelectedStaff.firstName + ' ' + this.state.editedSelectedStaff.lastName :
            this.state.selectedStaff && this.state.selectedStaff.firstName + ' ' + this.state.selectedStaff.lastName
        } /
        {
          this.props.editMode ?
            this.state.editedSelectedStaff && this.state.editedSelectedStaff.phone :
            this.state.selectedStaff && this.state.selectedStaff.phone
        }
      </Chip>
    );
  }

  private handleRequestDelete = (): void =>
    this.setState((prevState) =>
      ({
        selectedMember: this.props.editMode ? prevState.selectedMember : undefined,
        editedSelectedMember: this.props.editMode ? undefined : prevState.editedSelectedMember,
        searchText: ''
      })
    )

  private handleRequestStaffDelete = (): void =>
    this.setState((prevState) => 
      ({
        selectedStaff: this.props.editMode ? prevState.selectedStaff : undefined,
        editedSelectedStaff: this.props.editMode ? undefined : prevState.editedSelectedStaff,
        searchStaffText: ''
      })
    )

  private createColorCss = () => {
    return {
      width: '36px',
      height: '14px',
      borderRadius: '2px',
      background: this.state.editedColorValue
    };
  }

  private handleEventDateChange = (time: Date, editMode: boolean): void => {
    if (editMode) {
      // TODO : Added errorExists here because for some unknown reason eventDate !== editedEventDate
      // in isAnyFieldEdited not trigerred. I'm guessing state is not updated properly when value on
      // DatePicker is changed. Fix this hack later.
      return this.setState({
        editedEventDate: time,
        errorExists: false
      });
    } else {
      return this.setState({
        eventDate: time
      });
    }
  }

  private createModeButtons = (): Array<JSX.Element> => [this.renderCancelButton(), this.renderCreateButton()];

  private editModeButtons = (): Array<JSX.Element> => {
    return [
      this.renderCancelButton(),
      this.renderDeleteButton(),
      this.renderEditButton()
    ];
  }

  private renderEditButton = (): JSX.Element => {
    return(
      <FlatButton
        label={'Edit'}
        onClick={this.handleEditEvent}
        disabled={this.state.errorExists || !this.isAnyFieldEdited()}
        keyboardFocused={true}
        primary={true}
      />
    );
  }

  private renderDeleteButton = (): JSX.Element => {
    return(
      <FlatButton
        label={'Delete'}
        onClick={this.handleDelete}
        disabled={cannotDelete(CALENDAR, this.props.authState, this.props.adminState)}
        secondary={true}
      />
    );
  }

  private renderCancelButton = (): JSX.Element => {
    return(
      <FlatButton
        label={'Cancel'}
        onClick={this.handleClose}
      />
    );
  }

  private renderCreateButton = (): JSX.Element => {
    return(
      <FlatButton
        label={'Create'}
        onClick={this.handleCreateEvent}
        disabled={this.state.errorExists || this.isAnyFieldEmpty()}
        keyboardFocused={true}
        primary={true}
      />
    );
  }

  private isAnyFieldEmpty = (): boolean => {
    const {
      title,
      startTime,
      endTime,
      eventDate
    } = this.state;

    return title === '' || !startTime || !endTime || !eventDate;
  }

  private isAnyFieldEdited = (): boolean => {
    const {
      title,
      editedTitle,
      note,
      editedNote,
      startTime,
      editedStartTime,
      endTime,
      editedEndTime,
      colorValue,
      editedColorValue,
      editedEventDate,
      eventDate,
      selectedMember,
      editedSelectedMember,
      selectedStaff,
      editedSelectedStaff
    } = this.state;

    return title !== editedTitle || note !== editedNote || colorValue !== editedColorValue ||
      startTime !== editedStartTime || endTime !== editedEndTime || eventDate !== editedEventDate ||
      selectedMember !== editedSelectedMember || selectedStaff !== editedSelectedStaff;
  }

  private handleEditEvent = (): void => {
    const { selectedEvent, updateCalendarEvent } = this.props;
    const { editedColorValue, editedTitle, editedStartTime, editedEndTime, editedEventDate, editedNote } = this.state;

    updateCalendarEvent(
      selectedEvent!.id!,
      editedColorValue,
      editedTitle,
      this.createDateForApi(editedEventDate!, editedStartTime!), 
      this.createDateForApi(editedEventDate!, editedEndTime!),
      editedNote,
      this.state.editedSelectedMember && this.state.editedSelectedMember.id,
      this.state.editedSelectedStaff && this.state.editedSelectedStaff.id
    ).then(response => this.handleClose());
  }

  private handleNoteChange = (event: React.FormEvent<{}>, newValue: string, editMode: boolean) => {
    if (editMode) {
      this.setState({
        editedNote: newValue,
        noteErrorMessage: isValidCalendarEventNote(newValue) ? '' : TITLE_NOTE_ERROR,
        errorExists: !isValidCalendarEventNote(newValue)
      });
    } else {
      this.setState({
        note: newValue,
        noteErrorMessage: isValidCalendarEventNote(newValue) ? '' : TITLE_NOTE_ERROR,
        errorExists: !isValidCalendarEventNote(newValue)
      });
    }
  }

  private handleStartTimeChange = (event: React.SyntheticEvent<{}>, time: Date, editMode: boolean) => {
    if (editMode) {
      this.setState(
        {editedStartTime: time},
        () => this.setState({
          errorExists: !this.areStartAndEndTimeValid(editMode),
          timeErrorMessage: !this.areStartAndEndTimeValid(editMode) ? DISPLAY_TIME_ERROR : ''
        })
      );
    } else {
      this.setState(
        {startTime: time},
        () => this.setState({
          errorExists: !this.areStartAndEndTimeValid(editMode),
          timeErrorMessage: !this.areStartAndEndTimeValid(editMode) ? DISPLAY_TIME_ERROR : ''
        })
      );
    }
  }

  private handleEndTimeChange = (event: React.SyntheticEvent<{}>, time: Date, editMode: boolean) => {
    if (editMode) {
      this.setState(
        {editedEndTime: time},
        () => this.setState({
          errorExists: !this.areStartAndEndTimeValid(editMode),
          timeErrorMessage: !this.areStartAndEndTimeValid(editMode) ? DISPLAY_TIME_ERROR : ''
        })
      );
    } else {
      this.setState(
        {endTime: time},
        () => this.setState({
          errorExists: !this.areStartAndEndTimeValid(editMode),
          timeErrorMessage: !this.areStartAndEndTimeValid(editMode) ? DISPLAY_TIME_ERROR : ''
        })
      );
    }
  }

  private areStartAndEndTimeValid = (editMode: boolean): boolean => {
    const { startTime, endTime, editedStartTime, editedEndTime } = this.state;

    if (editMode) {
      return editedStartTime && editedEndTime ? editedStartTime <= editedEndTime : false;
    } else {
      return startTime && endTime ? startTime <= endTime : false;
    }
  }

  private doesErrorExist = (state: States): boolean => {
    const { titleErrorMessage, colorValueErrorMessage } = state;
    return titleErrorMessage || colorValueErrorMessage ? true : false;
  }

  private hadleTitleChange = (event: React.FormEvent<{}>, newValue: string, editMode: boolean) => {
    if (editMode) {
      this.setState({
        editedTitle: newValue,
        titleErrorMessage: isAlphanumeric(newValue) ? '' : TITLE_NOTE_ERROR,
        errorExists: !isAlphanumeric(newValue)
      });
    } else {
      this.setState({
        title: newValue,
        titleErrorMessage: isAlphanumeric(newValue) ? '' : TITLE_NOTE_ERROR,
        errorExists: !isAlphanumeric(newValue)
      });
    }
  }

  private handleClose = () => {
    this.setState({
      open: false,
      title: '',
      editedTitle: '',
      titleErrorMessage: '',
      colorValue: DEFAULT_COLOR,
      editedColorValue: DEFAULT_COLOR,
      colorValueErrorMessage: '',
      errorExists: false,
      eventDate: undefined,
      editedEventDate: undefined,
      startTime: undefined,
      editedStartTime: undefined,
      endTime: undefined,
      editedEndTime: undefined,
      timeErrorMessage: '',
      note: '',
      editedNote: '',
      noteErrorMessage: '',
      searchStaffText: '',
      searchText: '',
      selectedMember: undefined,
      editedSelectedMember: undefined,
      selectedStaff: undefined,
      editedSelectedStaff: undefined
    });
  }

  private handleDelete = (): void => {
    const { selectedEvent, deleteCalendarEvent } = this.props;

    deleteCalendarEvent(selectedEvent!.id!).then(response => this.handleClose());
  }

  private handleCreateEvent = (): void => {
    const { colorValue, editedColorValue, title, eventDate, startTime, endTime, note } = this.state;
    const { createCalendarEvent } = this.props;

    if (!colorValue && !editedColorValue) {
      return this.setState({
        colorValueErrorMessage: 'Pick one of the event types',
        errorExists: true
      });
    }

    if (startTime && endTime) {
      createCalendarEvent(
        editedColorValue, 
        title, 
        this.createDateForApi(eventDate!, startTime), 
        this.createDateForApi(eventDate!, endTime), 
        note,
        this.state.selectedMember && this.state.selectedMember.id,
        this.state.selectedStaff && this.state.selectedStaff.id
      )
      .then(response => {
        if (this.props.idleState.expired) {
          return;
        } else {
          return this.handleClose();
        }
      });
    }
  }

  private createDateForApi = (eventDate: Date, restOfTime: Date): number => {
    return new Date(
      eventDate.getFullYear(),
      eventDate.getMonth(),
      eventDate.getDate(),
      restOfTime.getHours(),
      restOfTime.getMinutes(),
      restOfTime.getSeconds()
    ).getTime();
  }

  private handleClick = (): void => this.setState({displayColorPicker: !this.state.displayColorPicker});

  private handleDisplayColorPickerClose = (): void => this.setState({displayColorPicker: false});

  private handleChange = (color: Color) => this.setState({editedColorValue: color.hex});
}

export default CreateEventModalComponent;



// WEBPACK FOOTER //
// ./src/dashboard/create.event.modal.component.tsx